import type { MutableRefObject } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema, Mark } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	CONNECTOR_SOURCE_CHIP_NODE_NAME,
	connectorSourceChipNodeSpec,
} from './connector-source-chip-node';
import { connectorSourceHydrationKey } from './connector-source-hydration-key';
import { createConnectorSourceChipNodeView } from './create-chip-node-view';
import { navigateAroundConnectorSourceChip } from './navigate-around-connector-source-chip';

type DispatchableEditorView = Pick<EditorView, 'state' | 'dispatch'>;

export type ConnectorSourceEntry = {
	key: string;
	display: string;
	source?: string;
	iconUrl?: string;
};

export type ConnectorSourceInputRuleConfig = {
	knownSourcesRef: MutableRefObject<ConnectorSourceEntry[]>;
};

const hasObjectShape = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getConnectorSourceDisplay = (value: unknown): string => {
	if (!hasObjectShape(value) || !hasObjectShape(value.attrs)) {
		return '';
	}

	return typeof value.attrs.display === 'string' ? value.attrs.display : '';
};

const normalizeConnectorSourceJson = (_key: string, value: unknown): unknown => {
	if (hasObjectShape(value) && value.type === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
		return {
			type: 'text',
			text: getConnectorSourceDisplay(value),
		};
	}

	return value;
};

export const normalizeConnectorSourceDoc = (doc: DocNode): DocNode =>
	JSON.parse(JSON.stringify(doc, normalizeConnectorSourceJson));

/**
 * Given the text before the cursor, try to find a connector source name
 * at the end. Matching is case-insensitive against `entry.key`.
 * Returns the matched entry and the start offset, or null.
 */
export const findTrailingSource = (
	textBefore: string,
	sources: ConnectorSourceEntry[],
): { entry: ConnectorSourceEntry; name: string; startOffset: number } | null =>
	matchTrailingSource(textBefore, sources, fg('platform_ai_mate_connector_injected_prompts'));

const matchTrailingSource = (
	textBefore: string,
	sources: ConnectorSourceEntry[],
	injectedPromptsEnabled: boolean,
): { entry: ConnectorSourceEntry; name: string; startOffset: number } | null => {
	const lower = injectedPromptsEnabled ? undefined : textBefore.toLowerCase();
	for (const entry of sources) {
		const { key, display } = entry;
		// Only lowercase the candidate suffix: prepared prompts call this matcher at each word boundary.
		const matches = injectedPromptsEnabled
			? textBefore.slice(-key.length).toLowerCase() === key
			: lower?.endsWith(key);
		if (matches) {
			const startOffset = textBefore.length - key.length;
			if (startOffset === 0 || /\s/.test(textBefore[startOffset - 1])) {
				return { entry, name: display, startOffset };
			}
		}
	}
	return null;
};

const createSourceChip = (schema: Schema, entry: ConnectorSourceEntry, marks?: readonly Mark[]) =>
	schema.nodes[CONNECTOR_SOURCE_CHIP_NODE_NAME].create(
		{ source: entry.source ?? entry.key, display: entry.display, iconUrl: entry.iconUrl ?? null },
		null,
		marks,
	);

const hydrateConnectorSources = (tr: Transaction, sources: ConnectorSourceEntry[]): Transaction => {
	if (!sources.length) {
		return tr;
	}
	const injectedPromptsEnabled = fg('platform_ai_mate_connector_injected_prompts');
	tr.doc.descendants((node, pos) => {
		if (node.type.spec.code) {
			return false;
		}
		if (
			!node.isText ||
			!node.text ||
			node.marks.some(({ type }) => type.name === 'link' || type.spec.code)
		) {
			return;
		}
		// Work backwards so a longer name (e.g. Jira Service Management) is matched first.
		// Use the typing matcher at each completed word, including the end of the prompt.
		for (let end = node.text.length; end > 0; end--) {
			if (end < node.text.length && !/^(?:\s|[,;:!?)\]}]|\.(?:\s|$))/.test(node.text.slice(end))) {
				continue;
			}
			const match = matchTrailingSource(node.text.slice(0, end), sources, injectedPromptsEnabled);
			if (match) {
				tr.replaceWith(
					tr.mapping.map(pos + match.startOffset),
					tr.mapping.map(pos + end),
					createSourceChip(tr.doc.type.schema, match.entry, node.marks),
				);
				end = match.startOffset;
			}
		}
	});
	return tr;
};

export const deleteAdjacentConnectorSourceChip = (
	view: DispatchableEditorView,
	event: KeyboardEvent,
): boolean => {
	if (event.key !== 'Backspace') {
		return false;
	}

	const { state } = view;
	const { selection } = state;

	if (!selection.empty) {
		return false;
	}

	const { $from } = selection;
	const nodeBefore = $from.nodeBefore;

	if (nodeBefore?.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
		const chipEndPosition = $from.pos;
		const chipStartPosition = chipEndPosition - nodeBefore.nodeSize;

		event.preventDefault();
		view.dispatch(state.tr.delete(chipStartPosition, chipEndPosition));
		return true;
	}

	if (nodeBefore?.isText && nodeBefore.text === ' ') {
		const positionBeforeSpace = $from.pos - 1;
		const resolvedPositionBeforeSpace = state.doc.resolve(positionBeforeSpace);
		const nodeBeforeSpace = resolvedPositionBeforeSpace.nodeBefore;

		if (nodeBeforeSpace?.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
			const chipStartPosition = positionBeforeSpace - nodeBeforeSpace.nodeSize;

			event.preventDefault();
			view.dispatch(state.tr.delete(chipStartPosition, $from.pos));
			return true;
		}
	}

	return false;
};

export const handleConnectorSourceTextInput = ({
	view,
	schema,
	from,
	text,
	sourceNames,
}: {
	view: DispatchableEditorView;
	schema: Schema;
	from: number;
	text: string;
	sourceNames: ConnectorSourceEntry[];
}): boolean => {
	if (!sourceNames.length) {
		return false;
	}

	const { state } = view;
	const { $from } = state.selection;
	const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc') + '';

	// When a delimiter (space, comma, etc.) is typed, match against textBefore
	// as before. When a word character is typed, match against textBefore + text
	// so the chip fires as soon as the last letter of the app name is complete —
	// mirroring the tip banner's "atEnd" (charAfter === undefined) logic.
	const isDelimiter = /\W/.test(text);

	// --- Chip upgrade: detect <chip> + " " + partial-text at cursor ---
	// textBefore already has \uFFFC for chip nodes. If the pattern matches,
	// try to upgrade the existing chip to a longer-named source.
	const chipSpaceTextMatch = /\uFFFC ([\w][\w ]*)$/.exec(textBefore);
	if (chipSpaceTextMatch) {
		const textAfterChipSpace = chipSpaceTextMatch[1];
		const fullTypedAfterChip = isDelimiter ? textAfterChipSpace : textAfterChipSpace + text;

		// Walk the parent's children to find the chip whose end is followed
		// by " " + textAfterChipSpace up to the cursor position.
		const parent = $from.parent;
		const parentStart = $from.start();
		const cursorParentOffset = $from.parentOffset;

		let chipPos = -1;
		let chipNode: PMNode | null = null;
		parent.forEach((child, childOffset) => {
			if (chipPos !== -1) {
				return; // already found
			}
			if (child.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
				const afterChipStart = childOffset + child.nodeSize;
				const textAfterChip = parent.textBetween(
					afterChipStart,
					cursorParentOffset,
					undefined,
					'\ufffc',
				);
				if (textAfterChip === ' ' + textAfterChipSpace) {
					chipPos = parentStart + childOffset;
					chipNode = child;
				}
			}
		});

		if (chipNode !== null && chipPos !== -1) {
			const chipDisplay = (chipNode as PMNode).attrs?.display ?? '';
			const candidate = (chipDisplay + ' ' + fullTypedAfterChip).toLowerCase();
			const existingChipKey = chipDisplay.toLowerCase();

			const upgradeMatch = sourceNames.find(
				(s) => s.key === candidate && s.key.length > existingChipKey.length,
			);

			if (upgradeMatch) {
				const newChip = createSourceChip(schema, upgradeMatch);
				const nodes = isDelimiter ? [newChip, schema.text(text)] : [newChip];
				const tr = state.tr.replaceWith(chipPos, from, nodes);
				view.dispatch(tr);
				return true;
			}
		}
	}
	// --- End chip upgrade ---

	const textToMatch = isDelimiter ? textBefore : textBefore + text;
	const match = findTrailingSource(textToMatch, sourceNames);
	if (!match) {
		// If no match and the node immediately before the cursor is a chip,
		// a word character has been typed that extends beyond the app name
		// (e.g. "miro" chip → user types "s" → should become plain "miros").
		// Replace the chip with its plain-text display name + the typed char.
		if (!isDelimiter) {
			const nodeBefore = $from.nodeBefore;
			if (nodeBefore?.type.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
				const chipDisplay = nodeBefore.attrs.display as string;
				const chipEnd = from;
				const chipStart = chipEnd - nodeBefore.nodeSize;
				const tr = state.tr.replaceWith(chipStart, chipEnd, schema.text(chipDisplay + text));
				view.dispatch(tr);
				return true;
			}
		}
		return false;
	}

	const absoluteStart = $from.start() + match.startOffset;
	const absoluteEnd = from;

	const connectorSourceChipNode = schema.nodes[CONNECTOR_SOURCE_CHIP_NODE_NAME];
	if (!connectorSourceChipNode) {
		return false;
	}

	const chipNode = createSourceChip(schema, match.entry);

	const nodes = isDelimiter ? [chipNode, schema.text(text)] : [chipNode];
	const tr = state.tr.replaceWith(absoluteStart, absoluteEnd, nodes);

	view.dispatch(tr);
	return true;
};

export const connectorSourceInputRulePlugin: NextEditorPlugin<
	'connectorSourceInputRule',
	{
		pluginConfiguration: ConnectorSourceInputRuleConfig;
	}
> = ({ config: { knownSourcesRef } }) => {
	return {
		name: 'connectorSourceInputRule',
		nodes() {
			return [{ name: CONNECTOR_SOURCE_CHIP_NODE_NAME, node: connectorSourceChipNodeSpec }];
		},
		pmPlugins() {
			return [
				{
					name: 'connectorSourceInputRule',
					plugin: ({ schema }) => {
						return new SafePlugin({
							...(fg('platform_ai_mate_connector_injected_prompts')
								? {
										key: connectorSourceHydrationKey,
										state: {
											init: () => ({}),
											apply: (tr, value) =>
												tr.getMeta('replaceDocument') || tr.getMeta(connectorSourceHydrationKey)
													? {}
													: value,
										},
										view: (view) => {
											const hydrate = () => {
												// Wait for the caller to finish inserting the prompt and setting its selection.
												void Promise.resolve().then(() => {
													if (!view.isDestroyed) {
														const tr = hydrateConnectorSources(
															view.state.tr,
															knownSourcesRef.current,
														);
														if (tr.docChanged) {
															view.dispatch(tr.setMeta('addToHistory', false));
														}
													}
												});
											};
											hydrate();
											return {
												update: (_, previousState) => {
													if (
														connectorSourceHydrationKey.getState(view.state) !==
														connectorSourceHydrationKey.getState(previousState)
													) {
														hydrate();
													}
												},
											};
										},
									}
								: {}),
							props: {
								// handleDOMEvents fires from the raw DOM event, before ProseMirror's
								// own keymap/baseKeymap plugin processes the event. This ensures our
								// arrow-key chip navigation intercepts ArrowRight/ArrowLeft before
								// ProseMirror's default atom-node selection logic runs.
								handleDOMEvents: {
									keydown(view: EditorView, event: KeyboardEvent): boolean {
										if (deleteAdjacentConnectorSourceChip(view, event)) {
											return true;
										}
										return navigateAroundConnectorSourceChip(view, event);
									},
								},
								nodeViews: {
									[CONNECTOR_SOURCE_CHIP_NODE_NAME]: (pmNode: PMNode): NodeView =>
										createConnectorSourceChipNodeView(pmNode),
								},
								handleTextInput(
									view: EditorView,
									from: number,
									_to: number,
									text: string,
								): boolean {
									return handleConnectorSourceTextInput({
										view,
										schema,
										from,
										text,
										sourceNames: knownSourcesRef.current,
									});
								},
							},
						});
					},
				},
			];
		},
	};
};
